import React from 'react';
import { Check } from 'lucide-react';

// Step interface
interface Step {
  label: string;
  description?: string;
  icon?: React.ReactNode;
}

// Stepper component props
interface StepperProps {
  steps: Step[];
  currentStep: number;
  colorScheme?: {
    active: string;
    completed: string;
    inactive: string;
    line: string;
  };
  showStepNumbers?: boolean;
  onStepClick?: (step: number) => void;
  allowClickNavigation?: boolean;
}

const Stepper: React.FC<StepperProps> = ({
  steps,
  currentStep,
  colorScheme = {
    active: 'border border-blue-600 bg-blue-600/20',
    completed: 'border border-blue-600 bg-blue-600',
    inactive: 'bg-gray-300/20 border border-gray-300',
    line: 'bg-gray-300',
  },
  onStepClick,
  allowClickNavigation = false,
}) => {
  const handleStepClick = (stepIndex: number) => {
    if (allowClickNavigation && onStepClick) {
      onStepClick(stepIndex + 1);
    }
  };

  const getStepStatus = (
    stepIndex: number
  ): 'completed' | 'active' | 'inactive' => {
    if (stepIndex + 1 < currentStep) return 'completed';
    if (stepIndex + 1 === currentStep) return 'active';
    return 'inactive';
  };

  const getStepColor = (status: string) => {
    switch (status) {
      case 'completed':
        return colorScheme.completed;
      case 'active':
        return colorScheme.active;
      default:
        return colorScheme.inactive;
    }
  };

  return (
    <div className="w-full md:max-w-[80%] mx-auto py-4">
      <div className="flex items-start justify-between">
        {steps.map((step, index) => {
          const status = getStepStatus(index);
          const isClickable =
            allowClickNavigation &&
            (status === 'completed' || status === 'active');

          return (
            <React.Fragment key={index}>
              {/* Step */}
              <div className="flex flex-col items-center flex-1">
                <div
                  onClick={() => isClickable && handleStepClick(index)}
                  className={`relative ${isClickable ? 'cursor-pointer' : ''}`}
                >
                  {/* Step Circle */}
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold border-[2px] ${getStepColor(
                      status
                    )} transition-all duration-300`}
                  >
                    {status === 'completed' && <Check size={20} />}
                  </div>
                </div>

                {/* Step Label */}
                <div className="mt-2 text-center">
                  <p
                    className={`text-sm font-medium ${
                      status === 'active'
                        ? 'text-blue-600'
                        : status === 'completed'
                        ? 'text-blue-600'
                        : 'text-gray-400'
                    }`}
                  >
                    {step.label}
                  </p>
                </div>
              </div>

              {/* Connector Line */}
              {index < steps.length - 1 && (
                <div className="flex-1 h-[2px] mb-7 relative mt-4">
                  <div
                    className={`h-full w-[calc(100%+100px)] -ml-[50px] transition-all duration-300 ${
                      getStepStatus(index) === 'completed'
                        ? colorScheme.completed
                        : colorScheme.line
                    }`}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default Stepper;
